import MetricHint from '../common/MetricHint';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { XMarkIcon, ChevronDownIcon, CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { configAPI, crmAPI, agentsAPI } from '../../services/api';
import ragService from '../../services/rag.service';
import { useAuthStore } from '../../store/authStore';
import { getTenants } from '../../services/tenant.service';

// NO DEFAULTS LIVE HERE. The backend owns them (GET /agents/defaults).
//
// A fallback copy was tempting — "so the form still renders if the API is down"
// — but the form cannot function without the API anyway: it fetches models,
// collections and prompt templates on mount, and creating an agent is a POST. A
// fallback would protect against nothing while quietly reintroducing the drift
// it exists to prevent. If we cannot reach the server, we say so and refuse,
// rather than create an agent with settings nobody chose.
//
// This is the bug that made the earlier fix useless: the form hardcoded
// top_k=5 / score_threshold=0.7 and sent them on every create, so the backend's
// defaults could never apply and a default got persisted as if it were a
// decision.

// Only fields backed by TenantAgent and its update route belong in this
// payload. Legacy generic-agent controls must not be sent as if they changed
// a live tenant agent.
const buildAgentSavePayload = (formData, selectedSystemPrompt, includeTenantId) => {
  const selectedCollections = formData.rag_config?.collection_names || [];
  const collectionName = selectedCollections[0] || formData.rag_config?.collection_name || '';
  const payload = {
    name: formData.name,
    description: formData.description,
    type: formData.type,
    model: formData.model,
    provider: formData.provider,
    temperature: formData.temperature,
    max_tokens: formData.max_tokens,
    top_p: formData.top_p,
    frequency_penalty: formData.frequency_penalty,
    presence_penalty: formData.presence_penalty,
    system_prompt: formData.system_prompt,
    prompt_template_name: selectedSystemPrompt || null,
    welcome_message: formData.welcome_message,
    enabled: formData.enabled,
    use_rag: formData.use_rag,
    rag_config: {
      collection_name: collectionName,
      top_k: formData.rag_config?.top_k,
      score_threshold: formData.rag_config?.score_threshold,
    },
  };
  if (includeTenantId) payload.tenant_id = formData.tenant_id;
  return payload;
};

const sameValue = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const AgentForm = ({ agent, availableModels, onSubmit, onCancel }) => {
  const { user } = useAuthStore();
  const isSuperadmin = user?.role === 'superadmin';
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'chat',
    model: '',
    provider: '',
    system_prompt: '',
    welcome_message: '',
    tools: [],
    capabilities: [],
    enabled: true,
    use_rag: true,  // Always true — collection binding is mandatory
    rag_config: {
      collection_name: '',  // Deprecated, kept for backward compatibility
      collection_names: [], // New: multiple collections
    },
    use_crm: false,
    crm_config: {
      enabled_endpoints: [],
      active_crm: '',
      auto_lookup_customer: false,
      include_customer_context: true,
      include_orders: true,
      include_tickets: true
    },
    workflow: null,
    workflow_id: '',
    tenant_id: ''  // For superadmin tenant selection
  });

  // Server-owned defaults. null until GET /agents/defaults answers — the form
  // does not render until then, so it can never send a number the operator did
  // not choose and the server did not supply.
  const [defaults, setDefaults] = useState(null);
  const [defaultsError, setDefaultsError] = useState(null);

  const [activeTab, setActiveTab] = useState('basic');
  const [modelsByProvider, setModelsByProvider] = useState({});
  const [providers, setProviders] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [crmStatus, setCrmStatus] = useState({ available_crms: [], active_crm: '', connection_status: {} });
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [loadingCRM, setLoadingCRM] = useState(false);
  const [collectionsDropdownOpen, setCollectionsDropdownOpen] = useState(false);
  const [collectionSearchTerm, setCollectionSearchTerm] = useState('');
  const collectionsDropdownRef = useRef(null);
  const [systemPrompts, setSystemPrompts] = useState([]);
  const [loadingSystemPrompts, setLoadingSystemPrompts] = useState(false);
  const [selectedSystemPrompt, setSelectedSystemPrompt] = useState('');
  const [initialSavePayload, setInitialSavePayload] = useState(null);
  const [promptsDropdownOpen, setPromptsDropdownOpen] = useState(false);
  const promptsDropdownRef = useRef(null);
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  // Ask the backend what a new agent's defaults are. The form must not invent
  // them: hardcoding top_k=5 / score_threshold=0.7 here meant the backend's
  // defaults could never apply, because the client sent its own on every create.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await agentsAPI.getAgentDefaults();
        const d = res?.data;
        if (cancelled || !d) return;
        setDefaults(d);
        // Only seed the form for a NEW agent. An existing agent's stored values
        // are the operator's choices and must not be overwritten by defaults.
        if (!agent) {
          setFormData((prev) => ({
            ...prev,
            temperature: d.temperature ?? prev.temperature,
            max_tokens: d.max_tokens ?? prev.max_tokens,
            top_p: d.top_p ?? prev.top_p,
            frequency_penalty: d.frequency_penalty ?? prev.frequency_penalty,
            presence_penalty: d.presence_penalty ?? prev.presence_penalty,
            rag_config: {
              ...prev.rag_config,
              top_k: d.rag_top_k ?? prev.rag_config.top_k,
              score_threshold: d.rag_score_threshold ?? prev.rag_config.score_threshold,
            },
          }));
        }
      } catch (err) {
        // Refuse rather than guess. An agent silently created with settings
        // nobody chose is exactly the failure this whole change exists to stop.
        if (!cancelled) setDefaultsError(err);
        console.error('Could not load agent defaults', err);
      }
    })();
    return () => { cancelled = true; };
  }, [agent]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (collectionsDropdownRef.current && !collectionsDropdownRef.current.contains(event.target)) {
        setCollectionsDropdownOpen(false);
      }
      if (promptsDropdownRef.current && !promptsDropdownRef.current.contains(event.target)) {
        setPromptsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load tenants for superadmin dropdown
  useEffect(() => {
    const loadTenants = async () => {
      if (!isSuperadmin) return;
      
      setTenantsLoading(true);
      try {
        const data = await getTenants(true); // Only active tenants
        setTenants(data.tenants || data || []);
      } catch (err) {
        console.error('Failed to load tenants:', err);
      } finally {
        setTenantsLoading(false);
      }
    };
    
    loadTenants();
  }, [isSuperadmin]);

  // Load models from config on mount
  useEffect(() => {
    const loadModelsFromConfig = async () => {
      try {
        setLoadingModels(true);
        const response = await configAPI.getLLMModels();
        
        // API returns { success: true, data: [...models with provider field] }
        const modelsArray = response.data || [];
        
        // Group models by provider
        const grouped = {};
        modelsArray.forEach(model => {
          const provider = model.provider || 'unknown';
          if (!grouped[provider]) grouped[provider] = [];
          grouped[provider].push(model);
        });
        
        setModelsByProvider(grouped);
        setProviders(Object.keys(grouped));
        
        console.log('Loaded models from config:', grouped);
      } catch (error) {
        console.error('Error loading models from config:', error);
        // Fallback to passed availableModels
        if (availableModels && availableModels.length > 0) {
          const grouped = {};
          availableModels.forEach(m => {
            const provider = m.provider || 'unknown';
            if (!grouped[provider]) grouped[provider] = [];
            grouped[provider].push(m);
          });
          setModelsByProvider(grouped);
          setProviders(Object.keys(grouped));
        }
      } finally {
        setLoadingModels(false);
      }
    };
    
    loadModelsFromConfig();
  }, [availableModels]);

  // Load available RAG collections
  useEffect(() => {
    const loadCollections = async () => {
      try {
        setLoadingCollections(true);
        const response = await ragService.getCollections();
        console.log('Loaded RAG collections:', response);
        setAvailableCollections(response.collections || []);
      } catch (error) {
        console.error('Error loading RAG collections:', error);
        setAvailableCollections([]);
      } finally {
        setLoadingCollections(false);
      }
    };
    
    loadCollections();
  }, []);

  // Load system prompts
  useEffect(() => {
    const loadSystemPrompts = async () => {
      try {
        setLoadingSystemPrompts(true);
        const response = await agentsAPI.getSystemPrompts();
        console.log('Loaded system prompts:', response.data);
        setSystemPrompts(response.data?.prompts || []);
      } catch (error) {
        console.error('Error loading system prompts:', error);
        setSystemPrompts([]);
      } finally {
        setLoadingSystemPrompts(false);
      }
    };
    
    loadSystemPrompts();
  }, []);

  // Load workflows
  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        setLoadingWorkflows(true);
        const response = await agentsAPI.getWorkflows();
        console.log('Loaded workflows:', response.data);
        setWorkflows(response.data?.workflows || []);
      } catch (error) {
        console.error('Error loading workflows:', error);
        setWorkflows([]);
      } finally {
        setLoadingWorkflows(false);
      }
    };
    
    loadWorkflows();
  }, []);

  // Load CRM status
  useEffect(() => {
    const loadCRMStatus = async () => {
      try {
        setLoadingCRM(true);
        const response = await crmAPI.getStatus();
        console.log('Loaded CRM status:', response.data);
        setCrmStatus(response.data || { available_crms: [], active_crm: '', connection_status: {} });
      } catch (error) {
        console.error('Error loading CRM status:', error);
        setCrmStatus({ available_crms: [], active_crm: '', connection_status: {} });
      } finally {
        setLoadingCRM(false);
      }
    };
    
    loadCRMStatus();
  }, []);

  // Update filtered models when provider changes
  useEffect(() => {
    if (formData.provider && modelsByProvider[formData.provider]) {
      setFilteredModels(modelsByProvider[formData.provider]);
    } else {
      setFilteredModels([]);
    }
  }, [formData.provider, modelsByProvider]);

  useEffect(() => {
    if (agent) {
      // Convert legacy collection_name to collection_names array if needed
      const ragConfig = agent.rag_config || {};
      let collectionNames = ragConfig.collection_names || [];
      if (collectionNames.length === 0 && ragConfig.collection_name) {
        collectionNames = [ragConfig.collection_name];
      }
      
      const nextFormData = {
        name: agent.name || '',
        description: agent.description || '',
        type: agent.type || 'chat',
        model: agent.model || '',
        provider: agent.provider || '',
        temperature: agent.temperature ?? defaults?.temperature,
        max_tokens: agent.max_tokens ?? defaults?.max_tokens,
        top_p: agent.top_p ?? defaults?.top_p,
        frequency_penalty: agent.frequency_penalty ?? defaults?.frequency_penalty,
        presence_penalty: agent.presence_penalty ?? defaults?.presence_penalty,
        system_prompt: agent.system_prompt || '',
        welcome_message: agent.welcome_message || '',
        tools: agent.tools || [],
        capabilities: agent.capabilities || [],
        enabled: agent.enabled !== undefined ? agent.enabled : true,
        use_rag: agent.use_rag || false,
        rag_config: {
          collection_name: ragConfig.collection_name || '',
          collection_names: collectionNames,
          top_k: ragConfig.top_k ?? defaults?.rag_top_k,
          score_threshold: ragConfig.score_threshold ?? defaults?.rag_score_threshold
        },
        use_crm: agent.use_crm || false,
        crm_config: agent.crm_config || {
          enabled_endpoints: [],
          active_crm: '',
          auto_lookup_customer: false,
          include_customer_context: true,
          include_orders: true,
          include_tickets: true
        },
        workflow: agent.workflow || null,
        workflow_id: typeof agent.workflow === 'string' ? agent.workflow : (agent.workflow?.workflow_id || '')
      };
      setFormData(nextFormData);
      
      // Restore selected system prompt template
      const templateName = agent.prompt_template_name || '';
      setSelectedSystemPrompt(templateName);
      setInitialSavePayload(buildAgentSavePayload(nextFormData, templateName, false));

      // Load workflow details if agent has a workflow
      const workflowId = typeof agent.workflow === 'string' ? agent.workflow : agent.workflow?.workflow_id;
      if (workflowId) {
        agentsAPI.getWorkflow(workflowId)
          .then(response => setSelectedWorkflow(response.data))
          .catch(err => console.error('Error loading agent workflow:', err));
      }
    } else {
      setSelectedSystemPrompt('');
      setInitialSavePayload(null);
    }
  }, [agent, defaults]);

  const [validationError, setValidationError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');

    // MANDATORY: At least one collection must be selected
    const selectedCollections = formData.rag_config?.collection_names || [];
    const singleCollection = formData.rag_config?.collection_name || '';
    if (selectedCollections.length === 0 && !singleCollection.trim()) {
      setValidationError('You must select at least one collection. Every agent must be bound to a document collection for data isolation.');
      setActiveTab('rag');
      return;
    }

    const hasPromptTemplate = Boolean(selectedSystemPrompt);
    const hasCustomPrompt = Boolean((formData.system_prompt || '').trim());
    if (!agent && !hasPromptTemplate && !hasCustomPrompt) {
      setValidationError('Select a system prompt template, or provide a custom system prompt before creating the agent.');
      setActiveTab('rag');
      return;
    }

    const submitData = buildAgentSavePayload(formData, selectedSystemPrompt, !agent);
    if (agent) {
      const changes = Object.fromEntries(
        Object.entries(submitData).filter(([key, value]) => !sameValue(value, initialSavePayload?.[key]))
      );
      // An unchanged edit must never become a write. This protects live agent
      // configuration even if a future edit response is accidentally partial.
      if (Object.keys(changes).length === 0) {
        toast('No changes to save');
        onCancel();
        return;
      }
      onSubmit(changes);
      return;
    }
    onSubmit(submitData);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRAGConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      rag_config: {
        ...prev.rag_config,
        [field]: value
      }
    }));
  };

  // Toggle collection selection for multi-select
  const handleCollectionToggle = (collectionName) => {
    setFormData(prev => {
      const currentCollections = prev.rag_config.collection_names || [];
      const newCollections = currentCollections.includes(collectionName)
        ? currentCollections.filter(c => c !== collectionName)
        : [...currentCollections, collectionName];
      return {
        ...prev,
        rag_config: {
          ...prev.rag_config,
          collection_names: newCollections
        }
      };
    });
  };

  const filteredCollections = useMemo(() => {
    const q = collectionSearchTerm.trim().toLowerCase();
    if (!q) return availableCollections;
    return availableCollections.filter((collection) => {
      const fields = [
        collection.name,
        collection.collection_name,
        collection.internal_name,
        collection.description,
        String(collection.documents_count || ''),
        String(collection.chunks_count || ''),
      ];
      return fields.some((field) => (field || '').toLowerCase().includes(q));
    });
  }, [availableCollections, collectionSearchTerm]);

  // Select/Deselect all collections
  const handleSelectAllCollections = (selectAll) => {
    const allNames = filteredCollections.map(c => c.name || c.collection_name).filter(Boolean);
    const currentCollections = formData.rag_config.collection_names || [];
    setFormData(prev => ({
      ...prev,
      rag_config: {
        ...prev.rag_config,
        collection_names: selectAll
          ? Array.from(new Set([...currentCollections, ...allNames]))
          : currentCollections.filter((name) => !allNames.includes(name))
      }
    }));
  };

  const handleCRMConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      crm_config: {
        ...prev.crm_config,
        [field]: value
      }
    }));
  };

  // Apply a system prompt from the dropdown
  const handleApplySystemPrompt = async (filename) => {
    if (!filename) {
      setSelectedSystemPrompt('');
      return;
    }
    
    try {
      const response = await agentsAPI.getSystemPrompt(filename);
      const promptData = response.data;
      
      setFormData(prev => ({
        ...prev,
        system_prompt: promptData.system_prompt || prev.system_prompt,
        temperature: promptData.temperature !== undefined ? promptData.temperature : prev.temperature
      }));
      
      setSelectedSystemPrompt(filename);
      console.log('Applied system prompt:', promptData.name);
    } catch (error) {
      console.error('Error loading system prompt:', error);
    }
  };
  const handleCRMEndpointToggle = (endpoint) => {
    setFormData(prev => {
      const endpoints = prev.crm_config.enabled_endpoints || [];
      const newEndpoints = endpoints.includes(endpoint)
        ? endpoints.filter(e => e !== endpoint)
        : [...endpoints, endpoint];
      return {
        ...prev,
        crm_config: {
          ...prev.crm_config,
          enabled_endpoints: newEndpoints
        }
      };
    });
  };

  const crmEndpoints = [
    { id: 'customers', label: 'Customer Lookup', description: 'Search and retrieve customer information' },
    { id: 'products', label: 'Products', description: 'Access product catalog data' },
    { id: 'orders', label: 'Orders', description: 'View order history and details' },
    { id: 'tickets', label: 'Support Tickets', description: 'Access support ticket information' },
    { id: 'customer_context', label: 'Full Customer Context', description: 'Get complete customer profile with orders and tickets' }
  ];

  const agentTypes = [
    { value: 'chat', label: 'Chat Assistant' },
    { value: 'code', label: 'Code Assistant' },
    { value: 'summarization', label: 'Summarization' },
    { value: 'analysis', label: 'Analysis' },
    { value: 'creative', label: 'Creative Writing' },
    { value: 'custom', label: 'Custom' }
  ];


  // Do not render the form until the server has told us its defaults. Rendering
  // early would mean showing (and on submit, SENDING) numbers that came from
  // nowhere — which is precisely how score_threshold=0.7 kept getting persisted
  // onto every new agent after the backend default had been changed to 0.5.
  if (defaultsError) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">
          Could not load agent defaults from the server.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Creating an agent now would use settings nobody chose. Check the API and retry.
        </p>
        <button
          onClick={onCancel}
          className="mt-4 px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600"
        >
          Close
        </button>
      </div>
    );
  }

  if (!defaults) {
    return (
      <div className="p-10 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        <span className="ml-3 text-sm text-gray-500">Loading agent defaults…</span>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 dialog-light">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {agent ? 'Edit Agent' : 'Create New Agent'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-8">
            {['basic', 'advanced', 'rag'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tab === 'rag' ? 'RAG Settings' : tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Basic Info Tab */}
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Tenant Selection - Superadmin Only */}
              {isSuperadmin && !agent && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    Target Tenant *
                  </label>
                  <select
                    name="tenant_id"
                    value={formData.tenant_id}
                    onChange={handleChange}
                    required
                    disabled={tenantsLoading}
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="">Select a tenant...</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name} ({tenant.slug})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-blue-700 mt-1">
                    As a platform admin, select which tenant this agent belongs to.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Customer Support Agent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {agentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe what this agent does..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Welcome Message
                  <MetricHint text="The greeting shown before the visitor types anything. Leave blank and one is auto-generated from the agent's opener pills." />
                </label>
                <textarea
                  name="welcome_message"
                  value={formData.welcome_message}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Hello! 👋 I'm ${formData.name || 'your assistant'}. How can I help you today?`}
                />
                <p className="text-xs text-gray-500 mt-1">The greeting message shown when users start a chat with this agent.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  System Prompt
                </label>
                <textarea
                  name="system_prompt"
                  value={formData.system_prompt}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="You are a helpful AI assistant..."
                />
                {selectedSystemPrompt && (
                  <p className="text-xs text-indigo-600 mt-1">
                    Template: {selectedSystemPrompt.replace('.json', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    <span className="text-gray-400 ml-1">(change in RAG Settings tab)</span>
                  </p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Enable this agent
                </label>
              </div>
            </div>
          )}

          {/* Advanced Parameters Tab */}
          {activeTab === 'advanced' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Top P (0-1)
                    <MetricHint text="An alternative way to control variety. Leave at 1. Changing both this and Temperature at once makes the agent's behaviour unpredictable — tune Temperature instead." />
                  </label>
                  <input
                    type="number"
                    name="top_p"
                    value={formData.top_p}
                    onChange={handleChange}
                    min="0"
                    max="1"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Nucleus sampling threshold
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Frequency Penalty (-2 to 2)
                    <MetricHint text="Discourages repeating the same words. Leave at 0. Raising it pushes the AI to reach for different tokens — and when those tokens are digits in a price, that is how a correct figure becomes a wrong one." />
                  </label>
                  <input
                    type="number"
                    name="frequency_penalty"
                    value={formData.frequency_penalty}
                    onChange={handleChange}
                    min="-2"
                    max="2"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Reduce repetition of token frequencies
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Presence Penalty (-2 to 2)
                    <MetricHint text="Pushes the AI toward new topics. Leave at 0. Your agent should stay on the question the visitor actually asked." />
                  </label>
                  <input
                    type="number"
                    name="presence_penalty"
                    value={formData.presence_penalty}
                    onChange={handleChange}
                    min="-2"
                    max="2"
                    step="0.1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Reduce repetition of topics
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* RAG Settings Tab */}
          {activeTab === 'rag' && (
            <div className="space-y-6">
              {/* Validation Error Banner */}
              {validationError && (
                <div className="bg-red-50 border border-red-300 rounded-md p-3 flex items-start gap-2">
                  <XMarkIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 font-medium">{validationError}</p>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-sm text-amber-800 font-medium">
                  ⚠ Collection binding is mandatory. Every agent must be linked to at least one document collection to ensure data isolation between tenants.
                </p>
              </div>

              <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                  {/* Multi-select Collections Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Collections to Search <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={collectionsDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setCollectionsDropdownOpen(!collectionsDropdownOpen)}
                        disabled={loadingCollections}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
                      >
                        <span className={formData.rag_config.collection_names?.length > 0 ? 'text-gray-900' : 'text-gray-500'}>
                          {loadingCollections 
                            ? 'Loading collections...' 
                            : formData.rag_config.collection_names?.length > 0
                              ? `${formData.rag_config.collection_names.length} collection(s) selected`
                              : 'Select collections...'}
                        </span>
                        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${collectionsDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Dropdown Panel */}
                      {collectionsDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                          <div className="p-2 border-b border-gray-200 bg-white">
                            <input
                              type="text"
                              value={collectionSearchTerm}
                              onChange={(e) => setCollectionSearchTerm(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              placeholder="Search collections by name, description, or internal name..."
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              autoFocus
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              {filteredCollections.length} of {availableCollections.length} collection(s) shown
                            </p>
                          </div>

                          {/* Select All / Deselect All */}
                          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex justify-between">
                            <button
                              type="button"
                              onClick={() => handleSelectAllCollections(true)}
                              className="text-xs text-blue-600 hover:text-blue-800"
                              disabled={filteredCollections.length === 0}
                            >
                              {collectionSearchTerm.trim() ? 'Select Visible' : 'Select All'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleSelectAllCollections(false)}
                              className="text-xs text-gray-600 hover:text-gray-800"
                              disabled={filteredCollections.length === 0}
                            >
                              {collectionSearchTerm.trim() ? 'Deselect Visible' : 'Deselect All'}
                            </button>
                          </div>
                          
                          {/* Collection Items */}
                          <div className="max-h-60 overflow-auto">
                            {availableCollections.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No collections available
                              </div>
                            ) : filteredCollections.length === 0 ? (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No collections match your search
                              </div>
                            ) : (
                              filteredCollections.map((collection) => {
                              const collectionName = collection.name || collection.collection_name;
                              const isSelected = formData.rag_config.collection_names?.includes(collectionName);
                              return (
                                <div
                                  key={collection.id || collectionName}
                                  onClick={() => handleCollectionToggle(collectionName)}
                                  className={`px-3 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-100 ${isSelected ? 'bg-blue-50' : ''}`}
                                >
                                  <div className="flex items-center">
                                    <div className={`w-4 h-4 mr-3 border rounded flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                      {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                                    </div>
                                    <div>
                                      <span className="text-sm font-medium text-gray-900">{collectionName}</span>
                                      <span className="text-xs text-gray-500 ml-2">
                                        ({collection.documents_count || 0} docs, {collection.chunks_count || 0} chunks)
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                              })
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Collections Tags */}
                    {formData.rag_config.collection_names?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formData.rag_config.collection_names.map((name) => (
                          <span
                            key={name}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {name}
                            <button
                              type="button"
                              onClick={() => handleCollectionToggle(name)}
                              className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {(!formData.rag_config.collection_names || formData.rag_config.collection_names.length === 0) && (
                      <p className="text-xs text-red-600 font-medium mt-1">
                        ⚠ Required — Every agent must be bound to at least one collection
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Top K Results
                        <MetricHint text="How many pieces of your content the AI reads before answering. Too low and it misses facts spread across several pages; too high and the answer gets diluted. 8-10 suits most knowledge bases." />
                      </label>
                      <input
                        type="number"
                        value={formData.rag_config.top_k}
                        onChange={(e) => handleRAGConfigChange('top_k', parseInt(e.target.value))}
                        min="1"
                        max="20"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Number of documents to retrieve
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Score Threshold (0-1)
                        <MetricHint text="How closely a piece of content must match the question to be used. Raise it and the AI says “I don’t have that information” more often — even when the answer IS in your content. Lower it and it considers more, at the risk of drifting. Start at 0.5 and adjust by testing real questions." />
                      </label>
                      <input
                        type="number"
                        value={formData.rag_config.score_threshold}
                        onChange={(e) => handleRAGConfigChange('score_threshold', parseFloat(e.target.value))}
                        min="0"
                        max="1"
                        step="0.1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum similarity score
                      </p>
                    </div>
                  </div>

                  {/* System Prompt Template Selector */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      System Prompt Template <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={promptsDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setPromptsDropdownOpen(!promptsDropdownOpen)}
                        disabled={loadingSystemPrompts}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-left flex items-center justify-between"
                      >
                        <span className={selectedSystemPrompt ? 'text-gray-900' : 'text-gray-500'}>
                          {loadingSystemPrompts 
                            ? 'Loading prompts...' 
                            : selectedSystemPrompt
                              ? '1 template selected'
                              : 'Select a system prompt template...'}
                        </span>
                        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${promptsDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Dropdown Panel */}
                      {promptsDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                          {/* Clear Selection Option */}
                          <div
                            onClick={() => {
                              handleApplySystemPrompt('');
                              setPromptsDropdownOpen(false);
                            }}
                            className={`px-3 py-2 cursor-pointer flex items-center hover:bg-gray-100 border-b border-gray-200 ${!selectedSystemPrompt ? 'bg-blue-50' : ''}`}
                          >
                            <div className={`w-4 h-4 mr-3 border rounded-full flex items-center justify-center ${!selectedSystemPrompt ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                              {!selectedSystemPrompt && <CheckIcon className="h-3 w-3 text-white" />}
                            </div>
                            <div className="flex-1">
                              <span className="text-sm text-gray-700">Custom prompt</span>
                              <p className="text-xs text-gray-500">
                                Custom templates are managed through tenant-admin Serviti Support.
                              </p>
                            </div>
                          </div>
                          
                          {/* Prompt Items */}
                          {systemPrompts.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              No system prompts available
                            </div>
                          ) : (
                            systemPrompts.map((prompt) => {
                              const isSelected = selectedSystemPrompt === prompt.filename;
                              return (
                                <div
                                  key={prompt.filename}
                                  onClick={() => {
                                    handleApplySystemPrompt(prompt.filename);
                                    setPromptsDropdownOpen(false);
                                  }}
                                  className={`px-3 py-2 cursor-pointer flex items-center hover:bg-gray-100 ${isSelected ? 'bg-blue-50' : ''}`}
                                >
                                  <div className={`w-4 h-4 mr-3 border rounded-full flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                    {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{prompt.name}</span>
                                    <span className="text-xs text-gray-500 ml-2">({prompt.category})</span>
                                    {prompt.description && (
                                      <p className="text-xs text-gray-400 mt-0.5">{prompt.description}</p>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Prompt Tag */}
                    {selectedSystemPrompt && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {systemPrompts.find(p => p.filename === selectedSystemPrompt)?.name || selectedSystemPrompt}
                          <button
                            type="button"
                            onClick={() => handleApplySystemPrompt('')}
                            className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Select a pre-defined system prompt optimized for RAG inference
                    </p>
                  </div>
                </div>
            </div>
          )}

          {/* CRM Integration Tab */}
          {activeTab === 'crm' && (
            <div className="space-y-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="use_crm"
                  checked={formData.use_crm}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm font-medium text-gray-900">
                  Enable CRM Integration
                </label>
              </div>

              {loadingCRM && (
                <div className="text-sm text-gray-500">Loading CRM configuration...</div>
              )}

              {formData.use_crm && !loadingCRM && (
                <div className="space-y-6 pl-6 border-l-2 border-green-200">
                  {/* CRM Status */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">CRM Status</h4>
                    {crmStatus.available_crms && crmStatus.available_crms.length > 0 ? (
                      <div className="space-y-2">
                        {crmStatus.available_crms.map((crm) => (
                          <div key={crm} className="flex items-center justify-between text-sm">
                            <span className="capitalize">{crm.replace('_', ' ')}</span>
                            <div className="flex items-center gap-2">
                              {crmStatus.active_crm === crm && (
                                <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                                  Active
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                crmStatus.connection_status?.[crm] 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {crmStatus.connection_status?.[crm] ? 'Connected' : 'Enabled'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-yellow-600">
                        No CRM configured. <a href="/crm-config" className="text-blue-600 underline">Configure a CRM</a> in the platform settings.
                      </p>
                    )}
                    {crmStatus.active_crm && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          System Active CRM: <span className="font-medium capitalize">{crmStatus.active_crm.replace('_', ' ')}</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Active CRM Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Active CRM for this Agent
                    </label>
                    <select
                      value={formData.crm_config.active_crm}
                      onChange={(e) => handleCRMConfigChange('active_crm', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">
                        {crmStatus.active_crm 
                          ? `Use Default (${crmStatus.active_crm.replace('_', ' ')})` 
                          : 'Select a CRM'}
                      </option>
                      {crmStatus.available_crms && crmStatus.available_crms.map((crm) => (
                        <option key={crm} value={crm}>
                          {crm.replace('_', ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          {crmStatus.active_crm === crm ? ' (System Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* CRM Endpoints */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Enabled CRM Endpoints
                    </label>
                    <div className="space-y-3">
                      {crmEndpoints.map((endpoint) => (
                        <div 
                          key={endpoint.id}
                          className="flex items-start p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                          onClick={() => handleCRMEndpointToggle(endpoint.id)}
                        >
                          <input
                            type="checkbox"
                            checked={formData.crm_config.enabled_endpoints?.includes(endpoint.id) || false}
                            onChange={() => handleCRMEndpointToggle(endpoint.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                          />
                          <div className="ml-3">
                            <label className="text-sm font-medium text-gray-900 cursor-pointer">
                              {endpoint.label}
                            </label>
                            <p className="text-xs text-gray-500">{endpoint.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CRM Options */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      CRM Options
                    </label>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.crm_config.auto_lookup_customer}
                        onChange={(e) => handleCRMConfigChange('auto_lookup_customer', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        Auto-lookup customer from email in conversation
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.crm_config.include_customer_context}
                        onChange={(e) => handleCRMConfigChange('include_customer_context', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        Include customer context in prompts
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.crm_config.include_orders}
                        onChange={(e) => handleCRMConfigChange('include_orders', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        Include order history in context
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.crm_config.include_tickets}
                        onChange={(e) => handleCRMConfigChange('include_tickets', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-700">
                        Include support tickets in context
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Workflow Tab */}
          {activeTab === 'workflow' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
                <h4 className="text-sm font-medium text-purple-900 mb-1">Agent Workflows</h4>
                <p className="text-xs text-purple-700">
                  Configure conversation workflows to guide the agent through specific stages like lead generation, 
                  customer onboarding, or support escalation.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Workflow
                </label>
                <select
                  value={formData.workflow_id || ''}
                  onChange={async (e) => {
                    const workflowId = e.target.value;
                    if (workflowId) {
                      try {
                        const response = await agentsAPI.getWorkflow(workflowId);
                        const workflowData = response.data;
                        setSelectedWorkflow(workflowData);
                        // Update both workflow_id and workflow field to ensure the workflow is saved
                        setFormData(prev => ({ 
                          ...prev, 
                          workflow_id: workflowId,
                          workflow: { workflow_id: workflowId }
                        }));
                      } catch (error) {
                        console.error('Error loading workflow:', error);
                        setSelectedWorkflow(null);
                        setFormData(prev => ({ ...prev, workflow_id: workflowId, workflow: workflowId }));
                      }
                    } else {
                      setSelectedWorkflow(null);
                      setFormData(prev => ({ ...prev, workflow_id: '', workflow: null }));
                    }
                  }}
                  disabled={loadingWorkflows}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">
                    {loadingWorkflows ? 'Loading workflows...' : 'No workflow (free-form conversation)'}
                  </option>
                  {workflows.map((workflow) => (
                    <option key={workflow.id} value={workflow.id}>
                      {workflow.name} {workflow.active ? '✓' : ''} ({workflow.stages_count} stages)
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Workflows define structured conversation flows with stages, data capture, and integrations.
                </p>
              </div>

              {/* Workflow Details */}
              {selectedWorkflow && (
                <div className="space-y-4 border border-purple-200 rounded-lg p-4 bg-white">
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-medium text-gray-900">{selectedWorkflow.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      selectedWorkflow.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedWorkflow.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600">{selectedWorkflow.description}</p>

                  {/* Workflow Stages */}
                  {selectedWorkflow.stages && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">Conversation Stages</h5>
                      <div className="space-y-2">
                        {Object.entries(selectedWorkflow.stages).sort((a, b) => 
                          (a[1].order || 0) - (b[1].order || 0)
                        ).map(([stageId, stage], index) => (
                          <div 
                            key={stageId}
                            className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div>
                            <div className="ml-3 flex-1">
                              <h6 className="text-sm font-medium text-gray-900 capitalize">
                                {stage.name || stageId}
                              </h6>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {stage.description || 'No description'}
                              </p>
                              {stage.data_to_capture && stage.data_to_capture.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {stage.data_to_capture.map((field) => (
                                    <span key={field} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            {index < Object.keys(selectedWorkflow.stages).length - 1 && (
                              <ArrowPathIcon className="w-4 h-4 text-gray-400 rotate-90 ml-2" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Schema */}
                  {selectedWorkflow.data_schema && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Data Fields to Capture</h5>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {Object.entries(selectedWorkflow.data_schema.lead || {}).map(([fieldName, fieldConfig]) => (
                          <div key={fieldName} className="flex items-center text-sm">
                            <span className={`w-2 h-2 rounded-full mr-2 ${fieldConfig.required ? 'bg-red-400' : 'bg-gray-300'}`}></span>
                            <span className="text-gray-700 capitalize">{fieldName.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-gray-400 ml-1">({fieldConfig.type})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Integrations */}
                  {selectedWorkflow.integrations && (
                    <div className="mt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Integrations</h5>
                      <div className="flex flex-wrap gap-2">
                        {Object.keys(selectedWorkflow.integrations).map((integration) => (
                          <span key={integration} className="px-3 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full capitalize">
                            {integration}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {workflows.length === 0 && !loadingWorkflows && (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <ArrowPathIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">No workflows configured yet.</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Workflows are defined in the platform-config/workflows folder.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {agent ? 'Update Agent' : 'Create Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AgentForm;
